import { type ThunkDispatch, createAsyncThunk } from "@reduxjs/toolkit"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import {
    CalculatorResultType,
    CoverageCheckResponseType,
} from "@common-shared/config/coverage.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { CHECKER_CONFIG } from "../../config/checker.config"
import { AmplitudeHelper } from "../../helpers/amplitude.helper"
import { CheckerHelper } from "../../helpers/checker.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { CheckerService } from "../../services/checker.service"
import type { IDynamicCheckerFormInputData } from "../../types/data.types"
import type { IThunkActionWithPracticeData } from "../../types/general.types"
import {
    checkerReceivedContinuousMonitoringCoverageResult,
    checkerReceivedMedicaidCoverageResult,
    checkerSetBulkCheckerRunId,
    checkerSetContinuousMonitoringInputData,
    checkerSetCoverageChecksSubmitted,
    checkerSetMedicaidInputData,
} from "../slices/checker.slice"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"
import type { RootState } from "../store"
import { userDetailsGetPracticeUsageQuota } from "./userDetails.thunks"

/**
 * Run bulk coverage checks for checker in GENERAL mode
 */
export const checkerRunGeneralChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunGeneralChecksPrefix", async (action, { dispatch }) => {
    await runNewBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        inputData => {
            dispatch(checkerSetContinuousMonitoringInputData(inputData))
        },
        runGeneralCoverageCheck,
        dispatch,
        "checkerRunGeneralChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Run bulk coverage checks for checker in SMART SCAN mode
 */
export const checkerRunSmartScanChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunSmartScanChecksPrefix", async (action, { dispatch }) => {
    await runNewBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        inputData => {
            dispatch(checkerSetContinuousMonitoringInputData(inputData))
        },
        runSmartScanCoverageCheck,
        dispatch,
        "checkerRunSmartScanChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Run bulk coverage checks for checker in DISCOVER mode
 */
export const checkerRunDiscoverChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunDiscoverChecksPrefix", async (action, { dispatch }) => {
    await runNewBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        inputData => {
            dispatch(checkerSetContinuousMonitoringInputData(inputData))
        },
        runDiscoverCoverageCheck,
        dispatch,
        "checkerRunDiscoverChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Run bulk coverage checks for checker in MEDICAID mode
 */
export const checkerRunMedicaidChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunMedicaidChecksPrefix", async (action, { dispatch }) => {
    await runNewBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        inputData => {
            dispatch(checkerSetMedicaidInputData(inputData))
        },
        runMedicaidCoverageCheck,
        dispatch,
        "checkerRunMedicaidChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Run bulk coverage checks for checker in AGENTIC PHONE mode
 */
export const checkerRunAgenticPhoneChecks = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>("checkerRunAgenticPhoneChecksPrefix", async (action, { dispatch }) => {
    await runNewBulkCoverageChecks<IDynamicCheckerFormInputData>(
        action.payload,
        action.practice,
        inputData => {
            dispatch(checkerSetContinuousMonitoringInputData(inputData))
        },
        runAgenticPhoneCoverageCheck,
        dispatch,
        "checkerRunAgenticPhoneChecksLoading",
        action.onSuccess,
        action.onError,
        action.onFinally,
    )
})

/**
 * Restore an existing single check result through SmartScan.
 */
export const checkerRestoreSinglePolicySmartScan = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        currentRequestId: string
        data: IDynamicCheckerFormInputData
    }>
>(
    "checkerRestoreSinglePolicySmartScanPrefix",
    async (action, { dispatch, getState }) => {
        dispatch(
            runtimeStartLoading("checkerRestoreSinglePolicySmartScanLoading"),
        )

        try {
            const rootState = getState() as RootState

            const result = await restorePolicySmartScan(
                action.payload.data,
                action.practice,
                action.payload.currentRequestId,
                dispatch,
                rootState.checker.manualCheckType === QuotaUsageType.MEDICAID,
            )

            if (result) {
                action.onSuccess?.(result)
            } else {
                ToastrHelper.warning(
                    "Unfortunately, Nirvana was not able to recover selected policy",
                )

                action.onError?.(result)
            }
        } catch (e) {
            action.onError?.()
        } finally {
            dispatch(
                runtimeStopLoading(
                    "checkerRestoreSinglePolicySmartScanLoading",
                ),
            )

            dispatch(
                userDetailsGetPracticeUsageQuota({
                    payload: action.practice,
                }),
            )

            action.onFinally?.()
        }
    },
)

/**
 * Bulk checker SmartScan retry. Shares the batch pacing with runNewBulkCoverageChecks but
 * not its run setup: a retry must keep the original bulkCheckerRunId, must not replace
 * the run's input data with its own subset, and must not log a second RAN_CHECK_BULK.
 */
export const checkerRestoreMultiplePoliciesSmartScan = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IDynamicCheckerFormInputData[]>
>(
    "checkerRestoreMultiplePoliciesSmartScanPrefix",
    async (action, { dispatch, getState }) => {
        const inputData = action.payload.filter(item => !!item)

        if (!inputData.length) {
            return
        }

        dispatch(
            runtimeStartLoading(
                "checkerRestoreMultiplePoliciesSmartScanLoading",
            ),
        )

        try {
            const isMedicaid =
                (getState() as RootState).checker.csvCheckType ===
                QuotaUsageType.MEDICAID

            await CheckerHelper.runChecksInBatches(inputData, data => {
                // CSV splits IN and OON into separate rows. Restore only
                // that row's network so one patient costs one SmartScan.
                const isInNetworkCheck =
                    isMedicaid || !!data.inNetworkCheck || !data.outNetworkCheck

                // A rejection would abort the rest of its batch, and one patient
                // failing to scan must not stop the others
                return restorePolicySmartScan(
                    data,
                    action.practice,
                    data.id,
                    dispatch,
                    isMedicaid,
                    isInNetworkCheck,
                    true,
                ).catch(() => undefined)
            })

            action.onSuccess?.()
        } catch (e) {
            action.onError?.()
        } finally {
            dispatch(
                runtimeStopLoading(
                    "checkerRestoreMultiplePoliciesSmartScanLoading",
                ),
            )

            dispatch(
                userDetailsGetPracticeUsageQuota({
                    payload: action.practice,
                }),
            )

            action.onFinally?.()
        }
    },
)

/**
 * Common method for running new checks in bulk
 */
const runNewBulkCoverageChecks = async <
    InputDataType extends {
        id?: string
        bulkCheckerRunId?: string
        isBulk?: boolean
    },
>(
    data: InputDataType[],
    practice: IPractice,
    onSetInputDataInState: (data: InputDataType[]) => void,
    runSingleCheck: (
        data: InputDataType,
        practice: IPractice,
        dispatch: ThunkDispatch<unknown, unknown, any>,
        triesCount?: number,
    ) => Promise<void>,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    runtimeLoaderIdentifier: string,
    onSuccess?: (data?: any) => void,
    onError?: (data?: any) => void,
    onFinally?: () => void,
) => {
    dispatch(runtimeStartLoading(runtimeLoaderIdentifier))

    try {
        // Sleep for X seconds here so breathing spinner stays longer during this process
        await UtilHelper.sleep(3)

        // Filter input data, it can have empty values
        // Need to copy these because by default they are immutable
        let inputData = JSON.parse(
            JSON.stringify(data.filter(item => !!item)),
        ) as InputDataType[]

        if (!inputData.length) {
            return
        }

        // Shared across every check of a run so its results can be found later.
        // CsvHelper stamps every parsed row isBulk and the manual form reuses this
        // runner with one unmarked row, so every() keeps a mixed payload from
        // getting a run id.
        const bulkCheckerRunId = inputData.every(item => item.isBulk)
            ? UtilHelper.generateUniqueVarchar(100)
            : undefined

        // Generate unique id for each of the input data array item
        // This id will be used for listening for results and mapping to needed input data
        try {
            inputData = inputData.map(item => {
                item.id = item.id || UtilHelper.generateUniqueVarchar(100)

                item.bulkCheckerRunId = bulkCheckerRunId

                return item
            })
        } catch (e) {
            console.log(e)
        }

        dispatch(checkerSetBulkCheckerRunId(bulkCheckerRunId || null))

        onSetInputDataInState(inputData)
        dispatch(checkerSetCoverageChecksSubmitted(true))

        await CheckerHelper.runChecksInBatches(inputData, item =>
            runSingleCheck(item, practice, dispatch),
        )

        if (data.length > 1) {
            AmplitudeHelper.logEvent(AmplitudeEvent.RAN_CHECK_BULK, {
                practiceId: practice.id,
                practiceName: practice.name,
                checks: data.length,
                runSuccess: true,
            })
        }

        onSuccess?.()
    } catch (e) {
        dispatch(checkerSetCoverageChecksSubmitted(false))

        if (data.length > 1) {
            AmplitudeHelper.logEvent(AmplitudeEvent.RAN_CHECK_BULK, {
                practiceId: practice.id,
                practiceName: practice.name,
                checks: data.length,
                runSuccess: false,
            })
        }

        onError?.()
    } finally {
        dispatch(runtimeStopLoading(runtimeLoaderIdentifier))

        dispatch(
            userDetailsGetPracticeUsageQuota({
                payload: practice,
            }),
        )

        onFinally?.()
    }
}

/**
 * Run single coverage check for GENERAL checker
 */
const runGeneralCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    triesCount = 1,
) => {
    if (!data.payer) {
        ToastrHelper.error("Payer is required for General check")

        return
    }

    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            CoverageCheckResponseType.COVERAGE,
            data.payer,
            data.memberId,
            data.inNetworkCheck,
        ),
        requestedHealthProvider: null,
        requestPayer: data.payer,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        // run here 2 checks if 2 checks were requested: inNetwork and outOfNetwork
        for (const networkType of ["inNetworkCheck", "outNetworkCheck"]) {
            if (!data[networkType as keyof IDynamicCheckerFormInputData]) {
                continue
            }

            const needRetry = triesCount < CHECKER_CONFIG.maxGetEstimateRetry

            const result = await CheckerService.runGeneralCoverageCheck(
                data,
                practice,
                needRetry,
                networkType === "inNetworkCheck",
            )

            if (!result && needRetry) {
                await runGeneralCoverageCheck(
                    data,
                    practice,
                    dispatch,
                    triesCount + 1,
                )
                return
            }

            dispatch(
                checkerReceivedContinuousMonitoringCoverageResult(
                    result || defaultErrorResult,
                ),
            )
        }
    } catch (e) {
        // If errored -> set default error result
        dispatch(
            checkerReceivedContinuousMonitoringCoverageResult(
                defaultErrorResult,
            ),
        )
    }
}

/**
 * Run single coverage check for SMART SCAN checker
 */
const runSmartScanCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
) => {
    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            CoverageCheckResponseType.SCAN,
            null,
            data.memberId,
            data.inNetworkCheck,
            CalculatorResultType.memberNotFound,
            true,
        ),
        requestedHealthProvider: null,
        requestPayer: null,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        // run here 2 checks if 2 checks were requested: inNetwork and outOfNetwork
        for (const networkType of ["inNetworkCheck", "outNetworkCheck"]) {
            if (!data[networkType as keyof IDynamicCheckerFormInputData]) {
                continue
            }

            const result = await CheckerService.runSmartScanCoverageCheck(
                data,
                practice,
                networkType === "inNetworkCheck",
                true,
            )

            dispatch(
                checkerReceivedContinuousMonitoringCoverageResult(
                    result || defaultErrorResult,
                ),
            )
        }
    } catch (e) {
        // If errored -> set default error result
        dispatch(
            checkerReceivedContinuousMonitoringCoverageResult(
                defaultErrorResult,
            ),
        )
    }
}

/**
 * Run single coverage check for DISCOVER checker
 */
const runDiscoverCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
) => {
    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            CoverageCheckResponseType.DISCOVER,
            null,
            data.memberId,
            data.inNetworkCheck,
            CalculatorResultType.memberNotFound,
        ),
        requestedHealthProvider: null,
        requestPayer: data.payer || null,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        // run here 2 checks if 2 checks were requested: inNetwork and outOfNetwork
        for (const networkType of ["inNetworkCheck", "outNetworkCheck"]) {
            if (!data[networkType as keyof IDynamicCheckerFormInputData]) {
                continue
            }

            const result = await CheckerService.runDiscoverCoverageCheck(
                data,
                practice,
                networkType === "inNetworkCheck",
                true,
            )

            dispatch(
                checkerReceivedContinuousMonitoringCoverageResult(
                    result || defaultErrorResult,
                ),
            )
        }
    } catch (e) {
        // If errored -> set default error result
        dispatch(
            checkerReceivedContinuousMonitoringCoverageResult(
                defaultErrorResult,
            ),
        )
    }
}

/**
 * Run single coverage check for medicaid checker
 */
const runMedicaidCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    triesCount = 1,
) => {
    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            CoverageCheckResponseType.MEDICAID,
            null,
            undefined,
            true,
        ),
        requestedHealthProvider: null,
        requestPayer: null,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        const needRetry = triesCount < CHECKER_CONFIG.maxGetEstimateRetry

        const result = await CheckerService.runMedicaidCoverageCheck(
            data,
            practice,
            needRetry,
        )

        if (!result && needRetry) {
            await runMedicaidCoverageCheck(
                data,
                practice,
                dispatch,
                triesCount + 1,
            )
            return
        }

        dispatch(
            checkerReceivedMedicaidCoverageResult(result || defaultErrorResult),
        )
    } catch (e) {
        // If errored -> set default error result
        dispatch(checkerReceivedMedicaidCoverageResult(defaultErrorResult))
    }
}

/**
 * Run single coverage check for AGENTIC PHONE checker
 */
const runAgenticPhoneCoverageCheck = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    triesCount = 1,
) => {
    if (!data.payer) {
        ToastrHelper.error("Payer is required for Agentic check")

        return
    }

    const defaultErrorResult: IOneVerifyCoverageCheckHistory = {
        requestData: CheckerService.getDefaultErrorCoverageRequest(
            practice,
            data,
        ),
        checkSpecificRequestData: {},
        coverageResult: CheckerService.getDefaultErrorCoverageResult(
            data.id,
            // Consider agentic all being COVERAGE type for now
            CoverageCheckResponseType.COVERAGE,
            data.payer,
            data.memberId,
            data.inNetworkCheck,
        ),
        requestedHealthProvider: null,
        requestPayer: data.payer,
        responsePayer: null,
        policy: null,
        agenticCheck: null,
        preAgenticCoverageResult: null,
    }

    try {
        // run here 2 checks if 2 checks were requested: inNetwork and outOfNetwork
        for (const networkType of ["inNetworkCheck", "outNetworkCheck"]) {
            if (!data[networkType as keyof IDynamicCheckerFormInputData]) {
                continue
            }

            const needRetry = triesCount < CHECKER_CONFIG.maxGetEstimateRetry

            const result = await CheckerService.runAgenticPhoneCoverageCheck(
                data,
                practice,
                needRetry,
                networkType === "inNetworkCheck",
            )

            if (!result && needRetry) {
                await runAgenticPhoneCoverageCheck(
                    data,
                    practice,
                    dispatch,
                    triesCount + 1,
                )
                return
            }

            dispatch(
                checkerReceivedContinuousMonitoringCoverageResult(
                    result || defaultErrorResult,
                ),
            )
        }
    } catch (e) {
        // If errored -> set default error result
        dispatch(
            checkerReceivedContinuousMonitoringCoverageResult(
                defaultErrorResult,
            ),
        )
    }
}

/**
 * Restore one existing result through SmartScan and update its checker state.
 * Shared by the manual and bulk checker.
 */
const restorePolicySmartScan = async (
    data: IDynamicCheckerFormInputData,
    practice: IPractice,
    currentRequestId: string,
    dispatch: ThunkDispatch<unknown, unknown, any>,
    isMedicaid: boolean,
    isInNetworkCheck?: boolean,
    ignoreToastErrors = false,
): Promise<IOneVerifyCoverageCheckHistory | undefined> => {
    const result = await CheckerService.runSmartScanCoverageCheck(
        data,
        practice,
        isInNetworkCheck,
        ignoreToastErrors,
    )

    // A failed recovery must not replace the existing checker result
    if (!result || result.coverageResult.isError) {
        return undefined
    }

    const recoveredResult: IOneVerifyCoverageCheckHistory = {
        ...result,

        coverageResult: {
            ...result.coverageResult,

            resultId: currentRequestId,
            isSmartScanResult: true,
        },
    }

    dispatch(
        isMedicaid
            ? checkerReceivedMedicaidCoverageResult(recoveredResult)
            : checkerReceivedContinuousMonitoringCoverageResult(
                  recoveredResult,
              ),
    )

    return result
}
